/**
 * discovery_panel.ts — THE SURVEY DESK window (space campaign slice F, 2026-09-30). Key: spaceSurvey (0 by default).
 *
 *   Sites   what I know of each site (observed → reported → assessed, with the age of that knowledge), my lease and its
 *           step (stabilize / extract), Lease / Release with the terms printed before I commit.
 *   Desk    at a survey desk: its array (the hazard census it publishes), its budget, its funded tenders (terms before
 *           Accept: funding, cargo size, route, risk, deadline, the insurance gap), Deliver, Hand in, Fund; elsewhere
 *           the boards of all three desks.
 *   Log     my expedition log, the consent to publish it (an hour late, the day only), other explorers' public logs.
 *   Intel   typed licences: the public previews (system, age, evidence, scope, price), mine, an offer form.
 *   Relay   The Silent Relay: my clues, the restoration and its choice.
 *
 * It renders only what the server lets this pilot see and calls the reducers; it decides nothing. THE TOKEN LAW: every
 * colour is an --ax-* token. THE FOCUSED-FIELD LAW: the 1 s refresh waits while one of its fields has focus.
 */
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from './ui_window.js';
import { showToast } from './toast.js';
import { keyHint } from './keybinds.js';
import { Identity } from 'spacetimedb';
import { discovery, tsMs, type ViewRow } from './discovery_client.js';
import { siteLine, leaseTerms, tenderTerms, postLine, handInLine, offerLine, ageText, untilText } from './discovery_ui.js';
import {
  RESEARCH_POSTS, researchPostOf, researchPostBits, censusSystems, INTEL_SCOPES, INTEL_SCOPE_LABEL, MYSTERY_KEY,
  RESTORE_UNITS, RESTORE_PAY_SOV, STABILIZE_UNITS, STABILIZE_DROP, SITE_LABEL, type SiteKind, POST_CHANNELS,
} from '@shared/space_survey.js';
import { prebuiltSystems } from '@shared/galaxy.js';

type Tab = 'sites' | 'desk' | 'log' | 'intel' | 'relay';
const TABS: Array<[Tab, string]> = [['sites', 'Sites'], ['desk', 'Desk'], ['log', 'Log'], ['intel', 'Intel'], ['relay', 'The Silent Relay']];
const SYS_NAMES = new Map(prebuiltSystems().map((s) => [s.id, s.name]));
const sysName = (id: string): string => SYS_NAMES.get(id) ?? id;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, text = '', css = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (text) e.textContent = text;
  if (css) e.style.cssText = css;
  return e;
}
const LINE = 'font:12px var(--ax-font);color:var(--ax-text);margin:3px 0;line-height:1.4';
const SOFT = 'font:11px var(--ax-font);color:var(--ax-soft);margin:2px 0 6px;line-height:1.4';
const HEAD = 'font:600 12px var(--ax-font);color:var(--ax-hi);margin:10px 0 4px;letter-spacing:.02em';
const CARD = 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);background:var(--ax-bg2);padding:6px 8px;margin:6px 0';

class SurveyDesk {
  private win: UiWindow | null = null;
  tab: Tab = 'sites';
  private timer = 0;
  /** form state survives the re-render (THE FOCUSED-FIELD LAW) */
  private fundAmt = '200';
  private offerSig = '';
  private offerScope = 'assessment';
  private offerPrice = '40';
  readonly census = { renders: 0, actions: [] as string[] };

  isOpen(): boolean { return !!this.win?.isOpen(); }
  toggle(tab?: Tab): void { if (this.isOpen() && (!tab || tab === this.tab)) this.close(); else this.open(tab); }
  open(tab?: Tab): void {
    discovery.ensure();
    if (tab) this.tab = tab;
    if (!this.win) {
      this.win = makeWindow({ id: 'survey-desk', title: 'Survey desk', icon: 'compass', width: Math.min(560, window.innerWidth - 24), height: Math.min(560, window.innerHeight - 96), minWidth: 300, minHeight: 220, onClose: () => this.close() });
      this.win.root.dataset.surveyDesk = '1';
      this.win.body.style.cssText += ';padding:8px 10px 12px;box-sizing:border-box;user-select:text';
    }
    this.win.setOpen(true);
    this.render();
    if (!this.timer) this.timer = window.setInterval(() => { if (this.isOpen()) this.render(); }, 1_000);
  }
  close(): void {
    this.win?.setOpen(false);
    if (this.timer) { clearInterval(this.timer); this.timer = 0; }
  }

  private act(label: string, p: Promise<void>, okText: string): void {
    this.census.actions.push(label);
    p.then(() => { showToast(okText, 'success'); this.render(true); }).catch((e: Error) => { showToast(String(e.message ?? e), 'error'); this.render(true); });
  }
  private btn(text: string, onClick: () => void, opts: { primary?: boolean; disabled?: boolean; title?: string; k?: string } = {}): HTMLButtonElement {
    const b = el('button', text);
    b.className = opts.primary ? 'ax-btn is-primary' : 'ax-btn';
    b.style.cssText = 'margin:3px 6px 3px 0;min-height:26px';
    if (opts.disabled) b.disabled = true;
    if (opts.title) b.title = opts.title;
    if (opts.k) b.dataset.k = opts.k;
    b.onclick = (e) => { e.stopPropagation(); onClick(); };
    return b;
  }
  private loc() {
    const c = atlas.conn, me = c?.identity;
    if (!c || !me) return null;
    try { return c.db.spaceLocation.identity.find(me) ?? null; } catch { return null; }
  }

  render(force = false): void {
    const w = this.win;
    if (!w || !w.isOpen()) return;
    const active = document.activeElement;
    if (!force && active && w.body.contains(active) && (active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.tagName === 'TEXTAREA')) return;
    this.census.renders++;
    const body = w.body;
    body.replaceChildren();
    const bar = el('div', '', 'display:flex;flex-wrap:wrap;gap:4px;margin:0 0 8px');
    bar.setAttribute('role', 'tablist');
    for (const [t, label] of TABS) {
      const b = this.btn(label, () => { this.tab = t; this.render(true); }, { primary: t === this.tab, k: `tab:${t}` });
      b.dataset.tab = t;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(t === this.tab));
      bar.appendChild(b);
    }
    body.appendChild(bar);
    const pane = el('div');
    pane.dataset.pane = this.tab;
    body.appendChild(pane);
    if (this.tab === 'sites') this.sites(pane);
    else if (this.tab === 'desk') this.desk(pane);
    else if (this.tab === 'log') this.log(pane);
    else if (this.tab === 'intel') this.intel(pane);
    else this.relay(pane);
  }

  // ── Sites ──
  private sites(root: HTMLElement): void {
    const now = Date.now();
    const loc = this.loc();
    root.appendChild(el('div', `Scan with ${keyHint('spaceScan')} (three rounds find a site). Hold the beam (${keyHint('spaceBeam')}) at a site to assess it — the server meters the work, one beat a second. Lease a site here, then hold the beam to extract.`, SOFT));
    const lease = discovery.lease();
    if (lease) {
      const card = el('div', '', CARD);
      card.dataset.lease = lease.sigKey;
      const kind = SITE_LABEL[lease.kind as SiteKind] ?? lease.kind;
      card.appendChild(el('div', `Your lease: the ${kind} in ${sysName(lease.systemId)} — ${untilText(tsMs(lease.expiresAt) - now)} left${lease.tenderId ? ` · on tender #${lease.tenderId}` : ` · bond ${lease.bondSov} ⓒ`}`, LINE));
      const step = lease.mode ? `${lease.mode === 'stabilize' ? 'Stabilizing' : 'Extracting'}: ${Math.round(100 * lease.progressMs / Math.max(1, lease.needMs))}%` : 'No step running — hold the beam at the site';
      card.appendChild(el('div', step, SOFT));
      const row = el('div');
      row.appendChild(this.btn(`Extract next${discovery.leaseMode === 'extract' ? ' ✓' : ''}`, () => { discovery.leaseMode = 'extract'; this.render(true); }, { primary: discovery.leaseMode === 'extract', k: 'mode:extract', title: 'The beam pulls the reserved output into your hold' }));
      row.appendChild(this.btn(`Stabilize next${discovery.leaseMode === 'stabilize' ? ' ✓' : ''}`, () => { discovery.leaseMode = 'stabilize'; this.render(true); }, { primary: discovery.leaseMode === 'stabilize', k: 'mode:stabilize', title: `Burns ${STABILIZE_UNITS} u of crystal from your hold; the hazard falls ${STABILIZE_DROP}` }));
      row.appendChild(this.btn('Release the lease', () => this.act('release', discovery.release(), 'Lease released — your bond is back'), { k: 'release' }));
      card.appendChild(row);
      root.appendChild(card);
    }
    const views = discovery.views().filter((v) => v.kind !== 'mystery');
    views.sort((a, b) => (a.systemId === loc?.systemId ? -1 : 0) - (b.systemId === loc?.systemId ? -1 : 0) || tsMs(a.recoverUntil) - tsMs(b.recoverUntil));
    root.appendChild(el('div', `Sites you know (${views.length})`, HEAD));
    if (!views.length) root.appendChild(el('div', 'None yet — the shared-stock sites are the ones seeded since the survey desks opened.', SOFT));
    const myTenders = discovery.tenders().filter((t) => t.state === 'accepted' && atlas.conn?.identity && t.acceptedBy.isEqual(atlas.conn.identity));
    for (const v of views) {
      const card = el('div', '', CARD);
      card.dataset.site = v.sigKey;
      card.appendChild(el('div', siteLine(this.facts(v), now, sysName(v.systemId)), LINE));
      const here = loc?.systemId === v.systemId && loc?.mode === 'flight';
      if (v.leasedBy !== 'me' && v.state === 'available' && !lease) {
        const td = myTenders.find((t) => t.sigKey === v.sigKey);
        card.appendChild(el('div', leaseTerms(v.kind, v.systemId, !!td), SOFT));
        card.appendChild(this.btn(td ? `Lease on tender #${td.tenderId}` : 'Lease (bond)', () => this.act('lease', discovery.leaseSite(v.sigKey, td ? td.tenderId : 0n), 'Leased — hold the beam at the site'), { primary: true, disabled: !here, title: here ? '' : 'Fly into its system first', k: `lease:${v.sigKey}` }));
      }
      root.appendChild(card);
    }
  }
  private facts(v: ViewRow) {
    return {
      sigKey: v.sigKey, systemId: v.systemId, kind: v.kind, stage: v.stage, state: v.state, stock: v.stock, hazard: v.hazard, band: v.band,
      knownAtMs: tsMs(v.knownAt), source: v.source, leasedBy: v.leasedBy, leaseExpiresMs: tsMs(v.leaseExpiresAt), recoverUntilMs: tsMs(v.recoverUntil), firstByMe: v.firstByMe,
    };
  }

  // ── Desk ──
  private balanceOf(stationKey: string): number | null {
    const i = RESEARCH_POSTS.findIndex((p) => p.key === stationKey);
    if (i < 0) return null;
    try { const w = atlas.conn?.db.wallet.identity.find(new Identity(researchPostBits(i))); return w ? Number(w.sov) : 0; } catch { return null; }
  }
  private desk(root: HTMLElement): void {
    const now = Date.now();
    const loc = this.loc();
    const me = atlas.conn?.identity ?? null;
    const atPost = loc?.mode === 'docked' ? researchPostOf(loc.stationKey) : null;
    const tenders = discovery.tenders();
    const knownBand = (sigKey: string): string => discovery.view(sigKey)?.band ?? '';
    const tenderCard = (t: (typeof tenders)[number], here: boolean): HTMLElement => {
      const card = el('div', '', CARD);
      card.dataset.tender = String(t.tenderId);
      const mine = !!me && t.acceptedBy.isEqual(me);
      for (const line of tenderTerms({ tenderId: t.tenderId, postName: researchPostOf(t.postKey)?.name ?? t.postKey, systemId: t.systemId, rewardSov: Number(t.rewardSov), state: t.state, deadlineMs: tsMs(t.deadline), acceptedByMe: mine, band: knownBand(t.sigKey) }, now, sysName(t.systemId))) card.appendChild(el('div', line, LINE));
      card.appendChild(el('div', `Tender #${t.tenderId} · ${t.state === 'accepted' ? (mine ? 'accepted by you' : 'accepted by another pilot') : t.state}`, SOFT));
      if (here) {
        const row = el('div');
        if (t.state === 'open') row.appendChild(this.btn('Accept', () => this.act('accept', discovery.accept(t.tenderId), 'Tender accepted — find the relay, lease the site on this tender'), { primary: true, k: `accept:${t.tenderId}` }));
        if (t.state === 'open' || mine) row.appendChild(this.btn('Deliver the relay', () => this.act('deliver', discovery.deliver(t.tenderId), 'Relay delivered — the escrow is paid'), { primary: mine, k: `deliver:${t.tenderId}` }));
        if (mine) row.appendChild(this.btn('Abandon', () => this.act('abandon', discovery.abandon(t.tenderId), 'Tender abandoned — it reopens for others'), { k: `abandon:${t.tenderId}` }));
        card.appendChild(row);
      } else card.appendChild(el('div', `Dock at ${researchPostOf(t.postKey)?.name.replace(' survey desk', '') ?? t.postKey} to accept or deliver.`, SOFT));
      return card;
    };
    if (atPost) {
      const post = discovery.post(atPost.key);
      root.appendChild(el('div', atPost.name, HEAD));
      const pl = el('div', postLine(atPost.name, post?.channelsLit ?? 1, this.balanceOf(atPost.key), post?.relaysReceived ?? 0, post?.status ?? ''), LINE);
      pl.dataset.post = atPost.key;
      root.appendChild(pl);
      const covered = censusSystems(atPost, post?.channelsLit ?? 1).map(sysName);
      root.appendChild(el('div', `The array's hazard census covers ${covered.join(', ')} (${post?.channelsLit ?? 1} of ${POST_CHANNELS} channels). Each relay delivered intact lights another channel; channels drift dark.`, SOFT));
      root.appendChild(this.btn('Read the hazard census', () => this.act('census', discovery.census(atPost.key), 'Census read — the bands are in your Sites tab'), { k: 'census' }));
      if (atPost.key === 'sys0:st3') root.appendChild(this.btn('Read the relay log', () => this.act('relaylog', discovery.readRelayLog(), 'An odd entry in the relay log — see The Silent Relay tab'), { k: 'relaylog' }));
      root.appendChild(el('div', 'Tenders at this desk', HEAD));
      const here = tenders.filter((t) => t.postKey === atPost.key && (t.state === 'open' || t.state === 'accepted'));
      if (!here.length) root.appendChild(el('div', post?.status ? `No tender: ${post.status}.` : 'No open tender right now.', SOFT));
      for (const t of here) root.appendChild(tenderCard(t, true));
      const done = tenders.filter((t) => t.postKey === atPost.key && t.state === 'delivered' && !!me && t.acceptedBy.isEqual(me));
      for (const t of done.slice(-3)) root.appendChild(el('div', `✓ Tender #${t.tenderId}: ${t.outcome} relay delivered — ${t.paidSov} ⓒ paid, receipt ${t.receipt}`, LINE));
      root.appendChild(el('div', handInLine(atPost.systemId), SOFT));
      root.appendChild(this.btn('Hand in a relay', () => this.act('handin', discovery.handIn(atPost.key), 'Relay handed in'), { k: 'handin' }));
      const fund = el('div', '', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-top:8px');
      const inp = el('input');
      inp.className = 'ax-input';
      inp.type = 'number'; inp.min = '1'; inp.max = '100000'; inp.value = this.fundAmt; inp.style.cssText = 'width:96px';
      inp.oninput = () => { this.fundAmt = inp.value; };
      fund.append(el('span', 'Fund the desk:', LINE), inp, this.btn('Fund', () => this.act('fund', discovery.fund(atPost.key, Math.max(1, Math.floor(Number(this.fundAmt) || 0))), 'Thank you — the desk can post more tenders'), { k: 'fund' }));
      root.appendChild(fund);
      return;
    }
    root.appendChild(el('div', `Three survey desks fund relay recoveries. Dock at one (${keyHint('spaceDock')}) to accept a tender, deliver, or read its hazard census.`, SOFT));
    for (const p of RESEARCH_POSTS) {
      const post = discovery.post(p.key);
      root.appendChild(el('div', postLine(p.name, post?.channelsLit ?? 1, this.balanceOf(p.key), post?.relaysReceived ?? 0, post?.status ?? ''), HEAD));
      const open = tenders.filter((t) => t.postKey === p.key && (t.state === 'open' || t.state === 'accepted'));
      if (!open.length) root.appendChild(el('div', 'No open tender.', SOFT));
      for (const t of open) root.appendChild(tenderCard(t, false));
    }
  }

  // ── Log ──
  private log(root: HTMLElement): void {
    const c = atlas.conn, me = c?.identity;
    if (!c || !me) return;
    const now = Date.now();
    let isPublic = false;
    try { isPublic = c.db.expeditionProfile.identity.find(me)?.isPublic ?? false; } catch { /* pre-publish */ }
    root.appendChild(el('div', 'Your expedition log keeps first discoveries, assessments, recoveries, deliveries, sales and clues — the system and the time, never a position.', SOFT));
    root.appendChild(this.btn(isPublic ? 'Stop publishing my log' : 'Publish my log (an hour late, the day only)', () => this.act('consent', discovery.setPublic(!isPublic), isPublic ? 'Your log is private again' : 'Your log will appear an hour late, with the day only'), { k: 'consent' }));
    let rows: Array<{ kind: string; text: string; at: { microsSinceUnixEpoch: bigint }; published: boolean }> = [];
    try { rows = [...c.db.expeditionLog.iter()].filter((r) => r.owner.isEqual(me)).sort((a, b) => Number(b.logId - a.logId)); } catch { /* pre-publish */ }
    root.appendChild(el('div', `Your log (${rows.length})`, HEAD));
    if (!rows.length) root.appendChild(el('div', 'Nothing yet.', SOFT));
    for (const r of rows.slice(0, 40)) {
      const d = el('div', `${ageText(now - tsMs(r.at))} · ${r.text}${r.published ? ' · public' : ''}`, LINE);
      d.dataset.log = r.kind;
      root.appendChild(d);
    }
    let pub: Array<{ owner: Identity; day: string; text: string; logId: bigint }> = [];
    try { pub = [...c.db.expeditionPublic.iter()].filter((r) => !r.owner.isEqual(me)).sort((a, b) => Number(b.logId - a.logId)); } catch { /* pre-publish */ }
    root.appendChild(el('div', `Other explorers (${pub.length})`, HEAD));
    for (const r of pub.slice(0, 20)) {
      let who = 'a pilot';
      try { const a = c.db.account.identity.find(r.owner); if (a) who = a.displayName || a.username; } catch { /* fine */ }
      root.appendChild(el('div', `${r.day} · ${who}: ${r.text}`, LINE));
    }
  }

  // ── Intel ──
  private intel(root: HTMLElement): void {
    const c = atlas.conn, me = c?.identity;
    if (!c || !me) return;
    const now = Date.now();
    root.appendChild(el('div', 'Sell what you observed: a coarse hazard report, a detailed assessment, or exact coordinates. A buyer sees the system, the age, the evidence and the price first; a sale keeps the ORIGINAL observation time and grants observation, never a lease.', SOFT));
    let offers: Array<(ReturnType<typeof c.db.intelOffer.offerId.find> & object)> = [];
    try { offers = [...c.db.intelOffer.iter()] as typeof offers; } catch { /* pre-publish */ }
    const open = offers.filter((o) => o.state === 'open');
    root.appendChild(el('div', `On offer (${open.filter((o) => !o.seller.isEqual(me)).length})`, HEAD));
    for (const o of open.filter((x) => !x.seller.isEqual(me))) {
      const row = el('div', '', CARD);
      row.dataset.offer = String(o.offerId);
      row.appendChild(el('div', offerLine(o.scope, sysName(o.systemId), tsMs(o.observedAt), now, o.evidence, Number(o.price)), LINE));
      row.appendChild(this.btn(`Buy (${o.price} ⓒ)`, () => this.act('buy', discovery.buy(o.offerId), 'Licence bought — the observation is yours'), { primary: true, k: `buy:${o.offerId}` }));
      root.appendChild(row);
    }
    root.appendChild(el('div', 'Your offers', HEAD));
    for (const o of open.filter((x) => x.seller.isEqual(me))) {
      const row = el('div', '', CARD);
      row.appendChild(el('div', `${offerLine(o.scope, sysName(o.systemId), tsMs(o.observedAt), now, o.evidence, Number(o.price))} · sold ${o.sold}`, LINE));
      row.appendChild(this.btn('Withdraw', () => this.act('withdraw', discovery.withdraw(o.offerId), 'Offer withdrawn — no new sales'), { k: `withdraw:${o.offerId}` }));
      root.appendChild(row);
    }
    // the offer form: sites I hold evidence for
    const mine = new Map<string, string>();
    for (const v of discovery.views()) if (v.kind !== 'mystery') mine.set(v.sigKey, `${SITE_LABEL[v.kind as SiteKind] ?? v.kind} · ${sysName(v.systemId)} (${v.stage})`);
    try { for (const r of c.db.sigReveal.iter()) if (r.identity.isEqual(me) && r.round >= 3 && r.sigKey.startsWith('sig:') && !mine.has(r.sigKey)) mine.set(r.sigKey, `${r.kind} · ${sysName(r.systemId)} (resolved)`); } catch { /* fine */ }
    const form = el('div', '', 'display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-top:8px');
    const site = el('select'); site.className = 'ax-input';
    for (const [k, label] of mine) { const o = el('option', label); o.value = k; site.appendChild(o); }
    if (this.offerSig && mine.has(this.offerSig)) site.value = this.offerSig; else this.offerSig = site.value;
    site.onchange = () => { this.offerSig = site.value; };
    const scope = el('select'); scope.className = 'ax-input';
    for (const s of INTEL_SCOPES) { const o = el('option', INTEL_SCOPE_LABEL[s]); o.value = s; scope.appendChild(o); }
    scope.value = this.offerScope; scope.onchange = () => { this.offerScope = scope.value; };
    const price = el('input'); price.className = 'ax-input'; price.type = 'number'; price.min = '1'; price.max = '10000'; price.value = this.offerPrice; price.style.cssText = 'width:80px';
    price.oninput = () => { this.offerPrice = price.value; };
    form.append(el('span', 'Offer:', LINE), site, scope, price, this.btn('Offer', () => this.act('offer', discovery.offer(this.offerSig, this.offerScope, Math.floor(Number(this.offerPrice) || 0)), 'Offered — buyers see the preview'), { disabled: !mine.size, k: 'offer' }));
    root.appendChild(form);
    let lic: Array<{ buyer: Identity; seller: Identity; scope: string; systemId: string; price: bigint; observedAt: { microsSinceUnixEpoch: bigint } }> = [];
    try { lic = [...c.db.intelLicence.iter()]; } catch { /* fine */ }
    root.appendChild(el('div', 'Licences', HEAD));
    for (const l of lic) root.appendChild(el('div', `${l.buyer.isEqual(me) ? 'Bought' : 'Sold'} · ${INTEL_SCOPE_LABEL[l.scope as keyof typeof INTEL_SCOPE_LABEL] ?? l.scope} · ${sysName(l.systemId)} · observed ${ageText(now - tsMs(l.observedAt))} · ${l.price} ⓒ`, LINE));
  }

  // ── The Silent Relay ──
  private relay(root: HTMLElement): void {
    const m = discovery.mystery();
    const clues = discovery.clues();
    const stage = !m ? 'The story has not begun.' : m.stage === 'restored' ? `Restored by ${m.restoredName}: ${m.serviceLine}` : m.stage === 'open' ? `Farlight Terminal's survey desk has funded the restoration (${m.escrowSov} ⓒ held for whoever finishes it).` : "Farlight Terminal's survey desk cannot fund the restoration yet — its budget is short.";
    const st = el('div', stage, LINE);
    st.dataset.mystery = m?.stage ?? 'none';
    root.appendChild(st);
    if (!clues.length) root.appendChild(el('div', "Farlight Terminal's survey desk keeps a relay log with an odd entry. Dock there and read it (Desk tab).", SOFT));
    for (const c of clues) {
      const card = el('div', '', CARD);
      card.dataset.clue = String(c.step);
      card.appendChild(el('div', `${c.step}. ${c.title}`, HEAD));
      card.appendChild(el('div', c.text, LINE));
      card.appendChild(el('div', `Source: ${c.source} · ${c.place}`, SOFT));
      card.appendChild(el('div', `${c.question} → ${c.nextLabel}${c.nextSystem ? ' (a beacon marks it on your HUD)' : ''}`, SOFT));
      root.appendChild(card);
    }
    if (discovery.hasClue(3) && m && m.stage !== 'restored') {
      root.appendChild(el('div', 'The restoration', HEAD));
      root.appendChild(el('div', `Bring ${RESTORE_UNITS} u of crystal to the relay and hold the beam 15 s. The desk pays ${RESTORE_PAY_SOV} ⓒ. Choose what the relay does after:`, SOFT));
      const row = el('div');
      row.appendChild(this.btn(`Public navigation aid${discovery.restoreChoice === 'public' ? ' ✓' : ''}`, () => { discovery.restoreChoice = 'public'; this.render(true); }, { primary: discovery.restoreChoice === 'public', k: 'choice:public', title: 'Every scanner in the Commons Reach recharges in 3 s instead of 5' }));
      row.appendChild(this.btn(`Private research access${discovery.restoreChoice === 'private' ? ' ✓' : ''}`, () => { discovery.restoreChoice = 'private'; this.render(true); }, { primary: discovery.restoreChoice === 'private', k: 'choice:private', title: 'Your assessments in the Commons Reach take half the time — only yours' }));
      root.appendChild(row);
      root.appendChild(el('div', discovery.restoreChoice === 'public' ? 'Public: every scanner in the Commons Reach recharges in 3 s (was 5). You give up keeping it to yourself.' : 'Private: your assessments in the Commons Reach take half the time. Nobody else gains.', SOFT));
    }
    void MYSTERY_KEY;
  }
}

export const surveyDesk = new SurveyDesk();

// Probe lever (read-only state + open/close): the browser leg reads what the desk shows without parsing pixels.
(globalThis as unknown as { __survey?: unknown }).__survey = {
  open: (tab?: Tab) => surveyDesk.open(tab),
  close: () => surveyDesk.close(),
  state: () => ({
    open: surveyDesk.isOpen(), tab: surveyDesk.tab, renders: surveyDesk.census.renders, actions: [...surveyDesk.census.actions],
    lease: (() => { const l = discovery.lease(); return l ? { sigKey: l.sigKey, mode: l.mode, progressMs: l.progressMs, needMs: l.needMs, tenderId: String(l.tenderId) } : null; })(),
    views: discovery.views().map((v) => ({ sigKey: v.sigKey, stage: v.stage, state: v.state, hazard: v.hazard, band: v.band, stock: v.stock, leasedBy: v.leasedBy, workMode: v.workMode, workMs: v.workMs })),
    leaseMode: discovery.leaseMode, stats: { ...discovery.stats },
  }),
  setLeaseMode: (m: 'stabilize' | 'extract') => { discovery.leaseMode = m; },
};
